import { QUOTE_STATUS_VALUES } from "@erp/shared";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FileSignature, Plus } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { buttonVariants } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { QUOTE_TONE } from "~/features/sales/status";
import { api, unwrap } from "~/lib/api";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { useInfiniteList } from "~/lib/use-list";

const search = z.object({
  q: z.string().optional(),
  status: z.enum(QUOTE_STATUS_VALUES).optional(),
  sort: z.enum(["issueDate", "number", "grossCents"]).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/quotes/")({ validateSearch: search, component: Quotes });

function fetchQuotes(params: Search & { offset: number; limit: number }) {
  return unwrap(api.quotes.get({ query: listQuery(params) }));
}
type Row = Awaited<ReturnType<typeof fetchQuotes>>["items"][number];

function Quotes() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const list = useInfiniteList("quotes", filters, fetchQuotes);
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const columns: Column<Row>[] = [
    {
      id: "number",
      header: t("quotes.fields.number"),
      sortKey: "number",
      cell: (r) => <span className="font-medium tabular">{r.number}</span>,
    },
    { id: "customer", header: t("quotes.fields.customer"), cell: (r) => r.customerName },
    {
      id: "date",
      header: t("quotes.fields.issueDate"),
      sortKey: "issueDate",
      cell: (r) => format.date(r.issueDate, "short"),
      className: "text-fg-2",
    },
    {
      id: "valid",
      header: t("quotes.fields.validUntil"),
      cell: (r) => format.date(r.validUntil, "short"),
      className: "text-fg-2",
      minWidth: "lg",
    },
    // Margins only for the roles that see costs (guide: roles).
    ...(can("costs:read")
      ? [
          {
            id: "margin",
            header: t("quotes.fields.margin"),
            align: "end",
            cell: (r) => (r.costCents && r.netCents ? format.percent((r.netCents - r.costCents) / r.netCents) : ""),
            className: "text-fg-2",
            minWidth: "xl",
          } satisfies Column<Row>,
        ]
      : []),
    {
      id: "total",
      header: t("quotes.fields.total"),
      sortKey: "grossCents",
      align: "end",
      cell: (r) => format.money(r.grossCents),
    },
    { id: "status", header: "", cell: (r) => <Badge tone={QUOTE_TONE[r.status]}>{t(`quotes.statuses.${r.status}`)}</Badge> },
  ];

  const newButton = can("sales:write") ? (
    <Link to="/quotes/new" className={buttonVariants({ variant: "primary" })}>
      <Plus />
      {t("quotes.new")}
    </Link>
  ) : null;

  return (
    <Page>
      <PageHeader
        title={t("quotes.title")}
        meta={list.isLoading ? null : t("common.table.rows", { count: list.total })}
        actions={newButton}
      />
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("quotes.searchPlaceholder")}
          />
          <Segmented
            label={t("common.fields.status")}
            value={filters.status ?? "all"}
            onChange={(v) => setSearch({ status: v === "all" ? undefined : (v as Search["status"]) })}
            options={[
              { value: "all", label: t("customers.all") },
              ...(["draft", "sent", "accepted", "expired"] as const).map((s) => ({ value: s, label: t(`quotes.statuses.${s}`) })),
            ]}
          />
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.customerName}</p>
                  <p className="text-xs text-fg-3 tabular">
                    {r.number} — {format.date(r.issueDate, "short")}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="tabular">{format.money(r.grossCents)}</p>
                  <Badge tone={QUOTE_TONE[r.status]}>{t(`quotes.statuses.${r.status}`)}</Badge>
                </div>
              </div>
            )}
            onOpen={(r) => void navigate({ to: "/quotes/$id", params: { id: r.id } })}
            sort={{ key: filters.sort, dir: filters.dir ?? "desc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            isDimmed={(r) => r.status === "cancelled" || r.status === "declined"}
            empty={
              filters.q || filters.status ? (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              ) : (
                <EmptyState
                  icon={<FileSignature />}
                  title={t("quotes.emptyTitle")}
                  body={t("quotes.emptyBody")}
                  action={newButton}
                />
              )
            }
          />
        )}
      </Panel>
    </Page>
  );
}
